/**
 * @file vuexstore for states used by the active draft post composer modal
 */
import type { DockableModalState } from 'types'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

interface ComposerModalState {
  modalState: DockableModalState
  scrolledToTop: boolean
  scrolledToBottom: boolean
  // flag to indicate whether the composer modal should focus on attachment caption on load
  shouldFocusAttachmentCaption: boolean
  xRequiresApprovalBanner: boolean
}

const initialState = (): ComposerModalState => {
  return {
    modalState: 'docked',
    scrolledToTop: false,
    scrolledToBottom: false,
    shouldFocusAttachmentCaption: false,
    xRequiresApprovalBanner: false,
  }
}

const composerModal: Module<ComposerModalState, RootState> = {
  namespaced: true,
  state: initialState(),
  getters: {
    modalState: (state): DockableModalState => state.modalState,
    isDocked: (state): boolean => state.modalState === 'docked',
    isExpanded: (state): boolean => state.modalState === 'expanded',
    scrolledToTop: (state): boolean => state.scrolledToTop,
    scrolledToBottom: (state): boolean => state.scrolledToBottom,
    shouldFocusAttachmentCaption: (state): boolean => state.shouldFocusAttachmentCaption,
    xRequiresApprovalBanner: (state): boolean => state.xRequiresApprovalBanner,
  },
  mutations: {
    DOCK_MODAL(state): void {
      state.modalState = 'docked'
    },
    EXPAND_MODAL(state): void {
      state.modalState = 'expanded'
    },
    UPDATE_SCROLL_POSITION(state, { scrolledToTop, scrolledToBottom }): void {
      state.scrolledToTop = scrolledToTop
      state.scrolledToBottom = scrolledToBottom
    },
    SET_SHOULD_FOCUS_ATTACHMENT_CAPTION(state): void {
      state.shouldFocusAttachmentCaption = true
    },
    CLEAR_SHOULD_FOCUS_ATTACHMENT_CAPTION(state): void {
      state.shouldFocusAttachmentCaption = false
    },
    SHOW_REQUIRES_APPROVAL_BANNER(state): void {
      state.xRequiresApprovalBanner = true
    },
    HIDE_REQUIRES_APPROVAL_BANNER(state): void {
      state.xRequiresApprovalBanner = false
    },
  },
  actions: {
    dock({ commit }): void {
      commit('DOCK_MODAL')
    },
    expand({ commit }): void {
      commit('EXPAND_MODAL')
    },
    updateScrollPosition({ commit }, { scrolledToTop, scrolledToBottom }): void {
      commit('UPDATE_SCROLL_POSITION', { scrolledToTop, scrolledToBottom })
    },
    setShouldFocusAttachmentCaption({ commit }): void {
      commit('SET_SHOULD_FOCUS_ATTACHMENT_CAPTION')
    },
    clearShouldFocusAttachmentCaption({ commit }): void {
      commit('CLEAR_SHOULD_FOCUS_ATTACHMENT_CAPTION')
    },
    showRequiresApprovalBanner({ commit }): void {
      commit('SHOW_REQUIRES_APPROVAL_BANNER')
    },
    hideRequiresApprovalBanner({ commit }): void {
      commit('HIDE_REQUIRES_APPROVAL_BANNER')
    },
  },
}

export default composerModal
export { ComposerModalState }
